import type {
  AgentBillingPlan,
  AgentBillingSubscriptionInterval,
  AgentSubscription,
  RateCard,
  SubscriptionItem,
  SubscriptionView,
} from "schemas/agent-billing";

import { PLAN_PRODUCT_TYPES } from "schemas/agent-billing";

import { price as priceUtils } from "../utils/price";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mapSubscriptionToView(
  subscription: AgentSubscription,
): SubscriptionView {
  return {
    subscriptionItems: subscription.items.map((item) => ({
      productType: item.productType,
      priceId: item.stripePriceId,
      quantity: item.quantity,
      interval: subscription.interval,
    })),
  };
}

function findPlanItem(items: SubscriptionItem[]): SubscriptionItem | undefined {
  return items.find((item) => PLAN_PRODUCT_TYPES.has(item.productType));
}

/**
 * Strict item lookup. When priceId is provided, both productType AND priceId
 * must match exactly — no lenient fallback. When priceId is omitted, matches
 * on productType only (first match wins).
 */
function findItem(
  items: SubscriptionItem[],
  productType: string,
  priceId?: string,
): SubscriptionItem | undefined {
  return items.find((item) => {
    if (item.productType !== productType) {
      return false;
    }
    if (priceId !== undefined) {
      return item.priceId === priceId;
    }
    return true;
  });
}

// Sentinel used by fromView to route into the private constructor path
const FROM_VIEW: unique symbol = Symbol("fromView");

export class SubscriptionDraft {
  readonly original: SubscriptionView;
  readonly current: SubscriptionView;

  constructor(source: AgentSubscription);
  constructor(source: SubscriptionDraft);
  constructor(source: typeof FROM_VIEW, view: SubscriptionView);
  constructor(
    source: AgentSubscription | SubscriptionDraft | typeof FROM_VIEW,
    view?: SubscriptionView,
  ) {
    if (source === FROM_VIEW && view) {
      this.original = Object.freeze(structuredClone(view));
      this.current = structuredClone(view);
    } else if (source instanceof SubscriptionDraft) {
      this.original = source.original;
      this.current = structuredClone(source.current);
    } else {
      // Legacy cast - may or may not be necessary
      // eslint-disable-next-line replo/no-as-cast
      const mapped = mapSubscriptionToView(source as AgentSubscription);
      this.original = Object.freeze(mapped);
      this.current = structuredClone(mapped);
    }
  }

  static fromView(view: SubscriptionView): SubscriptionDraft {
    return new SubscriptionDraft(FROM_VIEW, view);
  }

  // ---------------------------------------------------------------------------
  // Derived getters
  // ---------------------------------------------------------------------------

  get plan(): AgentBillingPlan | null {
    const item = findPlanItem(this.current.subscriptionItems);
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    return item ? (item.productType as AgentBillingPlan) : null;
  }

  get interval(): AgentBillingSubscriptionInterval {
    const item = findPlanItem(this.current.subscriptionItems);
    return item?.interval ?? "month";
  }

  get addOnItems(): SubscriptionItem[] {
    return this.current.subscriptionItems.filter(
      (item) => !PLAN_PRODUCT_TYPES.has(item.productType),
    );
  }

  get originalPlan(): AgentBillingPlan | null {
    const item = findPlanItem(this.original.subscriptionItems);
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    return item ? (item.productType as AgentBillingPlan) : null;
  }

  get originalInterval(): AgentBillingSubscriptionInterval {
    const item = findPlanItem(this.original.subscriptionItems);
    return item?.interval ?? "month";
  }

  // ---------------------------------------------------------------------------
  // Mutations — each returns a new SubscriptionDraft
  // ---------------------------------------------------------------------------

  withPlan(
    plan: AgentBillingPlan,
    { rateCard }: { rateCard: RateCard },
  ): SubscriptionDraft {
    const next = new SubscriptionDraft(this);
    const existingPlanItem = findPlanItem(next.current.subscriptionItems);
    const interval = existingPlanItem?.interval ?? this.interval;
    const existingPriceInRateCard = existingPlanItem
      ? rateCard.prices.find((priceEntry) => {
          return (
            priceEntry.priceId === existingPlanItem.priceId &&
            priceEntry.interval === interval
          );
        })
      : undefined;
    const priceId =
      existingPriceInRateCard?.priceId ??
      priceUtils.findDefaultByInterval({ prices: rateCard.prices, interval })
        ?.priceId ??
      "";

    if (existingPlanItem) {
      existingPlanItem.productType = plan;
      existingPlanItem.priceId = priceId;
    } else {
      next.current.subscriptionItems.unshift({
        productType: plan,
        priceId,
        quantity: 1,
        interval,
      });
    }

    return next;
  }

  withInterval(
    interval: AgentBillingSubscriptionInterval,
    { rateCard }: { rateCard: RateCard },
  ): SubscriptionDraft {
    const next = new SubscriptionDraft(this);
    // NOTE (Yuxin): Reset to original items so each item preserves its own
    // priceId. Without this, when multiple items share a productType (e.g.
    // grandfathered + standard pricing), toggling interval and back would
    // match all items to the first original, losing the other prices.
    if (next.original.subscriptionItems.length > 0) {
      next.current.subscriptionItems = structuredClone(
        next.original.subscriptionItems,
      );
    }

    for (const item of next.current.subscriptionItems) {
      if (item.interval === interval) {
        continue;
      }
      item.interval = interval;

      if (PLAN_PRODUCT_TYPES.has(item.productType)) {
        const existingPrice = rateCard.prices.find(
          (priceEntry) => priceEntry.priceId === item.priceId,
        );
        if (!existingPrice || existingPrice.interval !== interval) {
          item.priceId =
            priceUtils.findDefaultByInterval({
              prices: rateCard.prices,
              interval,
            })?.priceId ?? "";
        }
      } else {
        const addOnProduct = rateCard.addOnProducts.find(
          (product) => product.productType === item.productType,
        );
        const existingPrice = addOnProduct?.prices.find(
          (priceEntry) => priceEntry.priceId === item.priceId,
        );
        if (!existingPrice || existingPrice.interval !== interval) {
          item.priceId =
            priceUtils.findDefaultByInterval({
              prices: addOnProduct?.prices ?? [],
              interval,
            })?.priceId ?? item.priceId;
        }
      }
    }

    return next;
  }

  /**
   * Sets the quantity for an item identified by (productType, priceId).
   *
   * - Strictly matches the exact (productType, priceId) pair. This protects
   *   legacy/grandfathered items at different prices.
   * - If quantity <= 0 and a matching item exists, it is removed.
   * - If no matching item exists and quantity > 0, a new item is created.
   */
  withItemQuantity({
    productType,
    priceId,
    quantity,
  }: {
    productType: string;
    priceId: string;
    quantity: number;
  }): SubscriptionDraft {
    const next = new SubscriptionDraft(this);
    const existing = findItem(
      next.current.subscriptionItems,
      productType,
      priceId,
    );

    if (quantity <= 0) {
      if (existing) {
        next.current.subscriptionItems = next.current.subscriptionItems.filter(
          (item) => item !== existing,
        );
      }
      return next;
    }

    if (existing) {
      existing.quantity = quantity;
    } else {
      next.current.subscriptionItems.push({
        productType,
        priceId,
        quantity,
        interval: this.interval,
      });
    }

    return next;
  }

  /**
   * Adds 1 to the default price bucket for a product.
   */
  incrementProduct({
    productType,
    priceId,
  }: {
    productType: string;
    priceId: string;
  }): SubscriptionDraft {
    const currentQuantity = this.getItemQuantity({ productType, priceId });
    return this.withItemQuantity({
      productType,
      priceId,
      quantity: currentQuantity + 1,
    });
  }

  /**
   * Subtracts 1 from the preferred (default) price bucket first. If that
   * bucket is already at 0, cascades to other price buckets for the same
   * productType in stable array order.
   */
  decrementProduct({
    productType,
    preferredPriceId,
  }: {
    productType: string;
    preferredPriceId: string;
  }): SubscriptionDraft {
    const items = this.current.subscriptionItems.filter(
      (item) => item.productType === productType && item.quantity > 0,
    );
    const preferred = items.find((item) => item.priceId === preferredPriceId);
    const others = items.filter((item) => item.priceId !== preferredPriceId);
    const ordered = [...(preferred ? [preferred] : []), ...others];

    const target = ordered[0];
    if (!target) {
      return this;
    }
    return this.withItemQuantity({
      productType,
      priceId: target.priceId,
      quantity: target.quantity - 1,
    });
  }

  removeAllOfProductType(productType: string): SubscriptionDraft {
    if (
      !this.current.subscriptionItems.some(
        (item) => item.productType === productType,
      )
    ) {
      return this;
    }
    const next = new SubscriptionDraft(this);
    next.current.subscriptionItems = next.current.subscriptionItems.filter(
      (item) => item.productType !== productType,
    );
    return next;
  }

  // ---------------------------------------------------------------------------
  // Queries
  // ---------------------------------------------------------------------------

  getItemQuantity({
    productType,
    priceId,
  }: {
    productType: string;
    priceId?: string;
  }): number {
    return this.current.subscriptionItems
      .filter((item) => {
        if (item.productType !== productType) {
          return false;
        }
        if (priceId !== undefined) {
          return item.priceId === priceId;
        }
        return true;
      })
      .reduce((sum, item) => sum + item.quantity, 0);
  }

  hasChanges(): boolean {
    const current = this.current.subscriptionItems;
    const original = this.original.subscriptionItems;

    if (current.length !== original.length) {
      return true;
    }

    for (const item of current) {
      const originalItem = findItem(original, item.productType, item.priceId);
      if (!originalItem) {
        return true;
      }
      if (
        originalItem.quantity !== item.quantity ||
        originalItem.interval !== item.interval
      ) {
        return true;
      }
    }

    return false;
  }
}
